import * as React from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { Slot } from 'radix-ui';

import { cn } from '@components/lib/utils';

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default:
          'h-9 px-4 py-2 has-[>svg]:px-3 has-[>[data-slot=button-label]>svg]:px-3',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 has-[>[data-slot=button-label]>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5 has-[>[data-slot=button-label]>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4 has-[>[data-slot=button-label]>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

/* `forwardRef` so Radix `asChild` can pass a ref through to the rendered
 * button. Without this, parents like `<DropdownMenuTrigger asChild>` emit
 * "Function components cannot be given refs" on React 18 and click
 * handling silently breaks. */
type ButtonProps = React.ComponentPropsWithoutRef<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /* Show the in-flight state. Call sites used to render a spinner NEXT TO the
     * label, which left the old call to action readable while it was no longer
     * actionable and grew the button mid-click (the Settings save button went
     * 111px -> 127px and shifted 16px left). Here the label stays in the DOM
     * but hidden, so the button keeps its exact idle geometry, and the spinner
     * takes its place rather than joining it. */
    loading?: boolean;
  };

/* `visibility: hidden` on the label takes it out of the accessibility tree,
 * so a loading button would otherwise announce as a nameless busy control and
 * `getByRole('button', { name })` would stop matching it. Recover the name
 * from the text children and hand it back as an `aria-label`. */
const textFromChildren = (node: React.ReactNode): string =>
  React.Children.toArray(node)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') {
        return String(child);
      }
      if (React.isValidElement(child)) {
        const { children } = child.props as { children?: React.ReactNode };
        return textFromChildren(children);
      }
      return '';
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      asChild = false,
      loading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot.Root : 'button';
    /* `asChild` hands rendering to someone else's element, and Slot takes
     * exactly one child — there is nowhere to put a spinner. */
    const isLoading = loading && !asChild;
    const loadingLabel = isLoading ? textFromChildren(children) : '';

    return (
      <Comp
        ref={ref}
        data-slot="button"
        data-variant={variant}
        data-size={size}
        data-loading={isLoading || undefined}
        aria-busy={isLoading || undefined}
        aria-label={loadingLabel || undefined}
        disabled={asChild ? disabled : disabled || isLoading}
        className={cn(
          buttonVariants({ variant, size, className }),
          isLoading && 'relative',
        )}
        {...props}
      >
        {isLoading ? (
          <>
            {/* `contents` keeps the children in the button's own flex flow, so
             * the reserved width matches the idle button to the pixel; the
             * hidden visibility inherits down to them. The slot is what the
             * `has-[>[data-slot=button-label]>svg]` padding variants match, so
             * an icon button keeps its narrower idle padding while wrapped. */}
            <span
              aria-hidden
              data-slot="button-label"
              className="invisible contents"
            >
              {children}
            </span>
            <span className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="animate-spin" />
            </span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
